import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { morgansClient } from '../../../lib/connect/clients';
/**
 * Hook to manage saved opportunity status via morgans Connect RPC.
 *
 * `seed` primes the status from data the caller already has — the opportunities
 * list resolves each row's per-user saved status server-side (Opportunity.isSaved
 * / savedNotes, batch-looked-up), so a card that passes it in avoids a redundant
 * per-card GET. Without a seed (e.g. a standalone consumer) the hook fetches.
 */ export function useSavedOpportunity(noticeId, seed) {
    const queryClient = useQueryClient();
    const queryKey = [
        'opportunity',
        'saved',
        noticeId
    ];
    // Fetch current saved status — unless the caller seeded it from the list, in
    // which case we hold that value and skip the GET (a 20-card page would
    // otherwise fire 20 getSavedStatus calls for data the list already returned).
    const { data: savedStatus, isLoading: isLoadingStatus } = useQuery({
        queryKey,
        queryFn: async ()=>{
            const response = await morgansClient.getSavedStatus({
                noticeId
            });
            return {
                saved: response.saved,
                notes: response.notes || undefined
            };
        },
        staleTime: 5 * 60 * 1000,
        ...seed ? {
            initialData: {
                saved: seed.saved,
                notes: seed.notes
            },
            enabled: false
        } : {}
    });
    // Toggle saved status
    const toggleMutation = useMutation({
        mutationFn: async ({ saved, notes })=>{
            const response = await morgansClient.setSaved({
                noticeId,
                saved,
                notes: notes ?? ''
            });
            return {
                saved: response.saved,
                notes: response.notes
            };
        },
        onMutate: async ({ saved, notes })=>{
            await queryClient.cancelQueries({
                queryKey
            });
            const previousStatus = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, {
                saved,
                notes
            });
            return {
                previousStatus
            };
        },
        onError: (_err, _variables, context)=>{
            if (context?.previousStatus) {
                queryClient.setQueryData(queryKey, context.previousStatus);
            }
            // A seed observed mid-mutation was recorded as "seen" (see the sync
            // effect below) without being written to the cache, deferring to the
            // mutation while it was in flight. Now that it's failed and the cache
            // has been rolled back to the pre-mutation value, that deferred seed
            // is no longer superseded by anything more authoritative — clear the
            // marker so the sync effect treats the current seed as unapplied and
            // writes it once `isPending` clears, instead of skipping it forever.
            lastSyncedSeedRef.current = undefined;
        },
        // A seeded query is `enabled: false`, so onSettled's invalidateQueries below
        // cannot refetch to reconcile the optimistic write against what the server
        // actually returned — write the authoritative value directly instead.
        onSuccess: (data)=>{
            queryClient.setQueryData(queryKey, {
                saved: data.saved,
                notes: data.notes
            });
        },
        onSettled: ()=>{
            queryClient.invalidateQueries({
                queryKey
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'opportunities',
                    'saved'
                ]
            });
        }
    });
    // initialData only seeds the cache entry when it's first CREATED — if this
    // card re-renders with a CHANGED seed (the list refetched and now reports a
    // different isSaved/savedNotes), the existing entry is reused and the
    // disabled query has no way to pick it up on its own. Sync it in directly,
    // but never while a toggle is in flight — that would clobber the user's
    // just-clicked optimistic value with a now-stale list value.
    //
    // Compares against the LAST SEED WE'VE APPLIED (a ref), not the live cache
    // value: the cache legitimately diverges from seed the instant a toggle's
    // onMutate/onSuccess writes to it, and seed itself doesn't catch up until
    // the parent list refetches. Comparing to the cache would make this effect
    // fire the moment `isPending` flips back to `false` and stomp that fresh
    // write with the still-stale seed prop.
    const lastSyncedSeedRef = useRef(undefined);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally scoped to the seed values + mutation-pending flag — queryClient/queryKey are stable per noticeId, re-running on their identity would defeat the point of this sync.
    useEffect(()=>{
        if (!seed) return;
        const last = lastSyncedSeedRef.current;
        const changed = !last || last.saved !== seed.saved || last.notes !== seed.notes;
        if (!changed) return;
        if (toggleMutation.isPending) {
            // A seed that changes mid-mutation is necessarily a pre-mutation read —
            // the list can't yet know the mutation's outcome. Record it as "seen"
            // so we don't re-diff against a stale `last` once the mutation settles
            // (that would read as "seed changed again" and clobber the mutation's
            // committed value), but do NOT write it to the cache: while a toggle is
            // in flight, the optimistic/authoritative value belongs to the mutation.
            lastSyncedSeedRef.current = seed;
            return;
        }
        lastSyncedSeedRef.current = seed;
        queryClient.setQueryData(queryKey, {
            saved: seed.saved,
            notes: seed.notes
        });
    }, [
        seed?.saved,
        seed?.notes,
        toggleMutation.isPending
    ]);
    return {
        isSaved: savedStatus?.saved ?? false,
        notes: savedStatus?.notes,
        toggleSaved: (saved, notes)=>toggleMutation.mutate({
                saved,
                notes
            }),
        isLoading: isLoadingStatus || toggleMutation.isPending,
        error: toggleMutation.error
    };
}
